/**
 * The API returns at most this many rows per request, whatever `.limit()`
 * asks for (Supabase's "Max rows" setting). Anything that needs every row
 * pages through with `fetchAllPages`.
 */
export const PAGE_ROWS = 1000;

/** How many pages are in flight at once after the first. */
const CONCURRENCY = 4;

interface PageResult<T> {
  data: T[] | null;
  error: { message: string } | null;
  count?: number | null;
}

/**
 * Every row of a query, past the API's row cap. The first page asks for the
 * total, the rest are fetched a few at a time. `page(from, to, withCount)`
 * must order by a unique column last (e.g. `created_at` then `id`), or rows
 * that tie can land on two pages or none. A row that moves between pages
 * while this runs (something saved mid-load) is kept once.
 */
export async function fetchAllPages<T>(
  page: (from: number, to: number, withCount: boolean) => PromiseLike<PageResult<T>>,
  key: (row: T) => string,
  maxRows = 50_000,
): Promise<{ data: T[]; error: { message: string } | null }> {
  const first = await page(0, PAGE_ROWS - 1, true);
  if (first.error) return { data: [], error: first.error };
  const rows: T[] = [...(first.data ?? [])];
  const total = first.count ?? null;

  if (total !== null) {
    // The server's real cap may be lower than PAGE_ROWS: step by what it returned
    const step = rows.length;
    if (step > 0 && total > step) {
      const starts: number[] = [];
      for (let from = step; from < Math.min(total, maxRows); from += step) starts.push(from);
      for (let i = 0; i < starts.length; i += CONCURRENCY) {
        const batch = await Promise.all(starts.slice(i, i + CONCURRENCY).map((from) => page(from, from + step - 1, false)));
        for (const p of batch) {
          if (p.error) return { data: [], error: p.error };
          rows.push(...(p.data ?? []));
        }
      }
    }
  } else if (rows.length >= PAGE_ROWS) {
    // No total came back: walk until a short page
    for (let from = rows.length; from < maxRows; from += PAGE_ROWS) {
      const p = await page(from, from + PAGE_ROWS - 1, false);
      if (p.error) return { data: [], error: p.error };
      const got = p.data ?? [];
      rows.push(...got);
      if (got.length < PAGE_ROWS) break;
    }
  }

  const seen = new Set<string>();
  const unique = rows.filter((r) => {
    const k = key(r);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  return { data: unique, error: null };
}
